import {Close, Settings} from "@material-ui/icons";
import {Button, IconButton} from "@zendeskgarden/react-buttons";
import {Field, Hint, Label, MediaInput, Toggle} from "@zendeskgarden/react-forms";
import {Body, Footer, FooterItem, Header, Modal, Close as ModalClose, TooltipModal} from "@zendeskgarden/react-modals";
import {Tag} from "@zendeskgarden/react-tags";
import {Tooltip} from "@zendeskgarden/react-tooltips";
import {ReactComponent as ArrowLeft} from '@zendeskgarden/svg-icons/src/16/arrow-left-sm-fill.svg';
import {ReactComponent as DecimalFill} from '@zendeskgarden/svg-icons/src/16/decimal-fill.svg';
import {ReactComponent as SearchIcon} from '@zendeskgarden/svg-icons/src/16/search-stroke.svg';
import Handsontable from "handsontable";
import {BaseRenderer} from "handsontable/renderers";
import {memo, ReactElement, useEffect, useMemo, useRef, useState} from "react";
import {toast} from "react-toastify";
import {OriginalRevenueOperationalMetricValidKey, VersionType} from "../../../../../__generated__/generated_types";
import {CelebrateOptions, useVZConfetti} from "../../../../../components/vz-ui/vz-confetti/useVZConfetti";
import VZTwoButtonGroup from "../../../../../components/vz-ui/vz-two-button-group/VZTwoButtonGroup";
import {COLORS} from "../../../../../constants/Colors";
import {Property} from "../../../../../contexts/properties/PropertiesContext";
import {round} from "../../../../../utils/math";
import {getMonths} from "../../../../../utils/months-helper";
import {CompoundSimpleHotRow, SimpleHoTCellStyle, SimpleHotColumnSettings, SimpleHoTWrapper} from '../../../../admin/line-item-management/SimpleHoTWrapper';
import {SimpleHoTWrapperControls} from "../../../../admin/line-item-management/SimpleHoTWrapperControls";
import {IBatchUpsertInput} from "../../hooks/useUTSVOperationalMetrics";
import {getVersionLabel, IUTSVVersion} from "../../utils/operational-metric-helpers";
import {applyWholeUnitRounding, buildAPIPayloadFromChanges, buildFullUnitTypeData, buildSummaryRow, buildTableRowValues, calculateChangesFromTable, colPropToMonth, getExpirationPercentOfUnits, getFilteredHistoricalRows, roundAllValuesToWholeUnits, updateUnitTypeCellsAfterSummaryRowChange, validateOccupancyManagerInput} from '../logic/combined-metrics-logic';
import * as css from "../styles/combined-metrics.module.scss";
import {IMetricIssue} from "./metric-issues/logic/useMetricIssues";
import MetricIssues from "./metric-issues/MetricIssues";


const USE_AUTO_ROUNDING_KEY = "USE_AUTO_ROUNDING";

const LABEL_COL = 0;
const METRIC_COL = 1;
const MONTH_COLS = Array(12).fill(0).map((_, index) => index + 2);
const AVG_COL = 14;
const TOTAL_COL = 15;

const CONFETTI_CONFIG: CelebrateOptions = {
    preset: "burst",
    origin: {x: .95, y: 0},
    angle: 220,
    ticks: 80,
    startVelocity: 15,
    drift: -1,
    spread: 180,
    gravity: .85,
    zIndex: 1000,
}

export type AvailableMetrics =
    | OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount
    | OriginalRevenueOperationalMetricValidKey.RenewalRatio
    | OriginalRevenueOperationalMetricValidKey.MoveOutRate
    | OriginalRevenueOperationalMetricValidKey.OccupancyRate
    | OriginalRevenueOperationalMetricValidKey.MoveInCount;

export type MetricArrays = {
    [key in AvailableMetrics]: number[];
};

export type MetricSingleValues = {
    [key in AvailableMetrics]: number;
};

export const metricToLabelMap: Map<AvailableMetrics, string> = new Map([
    [OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount, "Expirations"],
    [OriginalRevenueOperationalMetricValidKey.RenewalRatio, "Renewals"],
    [OriginalRevenueOperationalMetricValidKey.MoveOutRate, "Move Outs"],
    [OriginalRevenueOperationalMetricValidKey.OccupancyRate, "Occupancy"],
    [OriginalRevenueOperationalMetricValidKey.MoveInCount, "Move Ins"],
]);

export interface FullUnitTypeDataCalcInput extends MetricArrays {
    unitTypeName: string;
    unitTypeCount: number;
    previousVersionLastMonthData: MetricSingleValues;
    versionType: VersionType;
    year: number;
}

export interface FullUnitTypeData {
    unitTypeName: string;
    unitTypeCount: number;
    versionType: VersionType;
    year: number;
    counts: MetricArrays;
    rates: MetricArrays;
    countAverages: MetricSingleValues;
    rateAverages: MetricSingleValues;
    countTotals: MetricSingleValues;
    rateTotals: MetricSingleValues;
    // to calculate move ins we need previous month occupancy, which can sometimes be from
    // previous year actuals, so to make it easier I'm just storing the last month occupancy value
    // for either actuals from budgetYear - 2 if versionType is rfcst, or last month occupancy of rfcst
    // if versionType is bdgt
    previousVersionLastMonthData: MetricSingleValues;
}

interface CombinedMetricsTableProps {
    property: Property;
    versionType: VersionType.Reforecast | VersionType.Budget;
    year: number;
    tableLocked: boolean;
    isLoading: boolean;
    unitVersionData: IUTSVVersion[];
    onChange?: (changes: IBatchUpsertInput[], triggerRefresh?: boolean) => void;
    controlsSlot?: ReactElement;
}

function getAutoRoundingPreference(): boolean {
    const stored: string | null = sessionStorage.getItem(USE_AUTO_ROUNDING_KEY);

    if (stored === null) {
        return false;
    }

    return stored === "true";
}

export default function CombinedMetricsTable({
    property,
    versionType,
    year,
    tableLocked,
    isLoading,
    unitVersionData,
    onChange,
    controlsSlot,
}: CombinedMetricsTableProps): ReactElement {
    const {celebrate} = useVZConfetti({cooldownMs: 0});

    const settingsButtonRef = useRef<HTMLButtonElement>(null);
    // boolean to inform the "all done you did it!" toast notification
    const issuesToSolve = useRef(false);
    // the new rounding setting doesnt actually change the table rows, so the memo doesnt pick it up
    // I'm putting it in a ref to make sure it gets picked up and used immediately, and across renders
    const roundToWholeUnitsRef = useRef(false);

    const [filterText, setFilterText] = useState("");
    const [tableRows, setTableRows] = useState<CompoundSimpleHotRow[]>([]);
    const [historicalTableRows, setHistoricalTableRows] = useState<CompoundSimpleHotRow[]>([]);
    const [fullUnitData, setFullUnitData] = useState<FullUnitTypeData[]>([]);
    const [visibleMetrics, setVisibleMetrics] = useState<Set<AvailableMetrics>>(new Set<AvailableMetrics>([
        OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount,
        OriginalRevenueOperationalMetricValidKey.RenewalRatio,
        OriginalRevenueOperationalMetricValidKey.OccupancyRate,
        OriginalRevenueOperationalMetricValidKey.MoveInCount,
    ]));
    const [showAsRates, setShowAsRates] = useState(true);
    const [autoRound, setAutoRound] = useState(getAutoRoundingPreference());
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [showRoundAllConfirmation, setShowRoundAllConfirmation] = useState(false);

    useEffect(() => {
        roundToWholeUnitsRef.current = autoRound;
    }, [autoRound]);

    useEffect(() => {
        const historicalActualsData = unitVersionData.filter(x => x.version === VersionType.Actuals).sortByDescending("year");
        const rfcstData = unitVersionData.find(x => x.version === VersionType.Reforecast);
        const bdgtData = unitVersionData.find(x => x.version === VersionType.Budget);
        const dataForCurrentYear = versionType === VersionType.Reforecast ? rfcstData : bdgtData;

        if (!dataForCurrentYear) {
            return;
        }

        const historicals: CompoundSimpleHotRow[] = [];
        let dataForYearBeforeReforecast: IUTSVVersion | undefined;

        if (versionType === VersionType.Budget && rfcstData) {
            historicalActualsData.unshift(rfcstData);
            const yearBeforeReforecast = year - 2;
            dataForYearBeforeReforecast = unitVersionData.find(x => x.version === VersionType.Actuals && x.year === yearBeforeReforecast);
        }
        const dataForYearBeforeReforecastUnitMap = new Map<string, MetricSingleValues>();
        for (const unitType of dataForYearBeforeReforecast?.unitTypes ?? []) {
            dataForYearBeforeReforecastUnitMap.set(unitType.unitTypeName, {
                leaseExpirationCount: unitType.leaseExpirationCount[11] ?? 0,
                renewalRatio: unitType.renewalRatio[11] ?? 0,
                moveOutRate: unitType.moveOutRate[11] ?? 0,
                occupancyRate: unitType.occupancyRate[11] ?? 0,
                moveInCount: unitType.moveInCount[11] ?? 0,
            });
        }
        for (const yearData of historicalActualsData) {
            const builtHistoricalRows = [];
            for (const unitType of yearData.unitTypes) {
                let previousVersionLastMonthData: MetricSingleValues = {
                    leaseExpirationCount: 0,
                    renewalRatio: 0,
                    moveOutRate: 0,
                    occupancyRate: 0,
                    moveInCount: 0,
                };
                if (yearData.version === VersionType.Reforecast) {
                    previousVersionLastMonthData = {
                        leaseExpirationCount: dataForYearBeforeReforecastUnitMap.get(unitType.unitTypeName)?.leaseExpirationCount ?? 0,
                        renewalRatio: dataForYearBeforeReforecastUnitMap.get(unitType.unitTypeName)?.renewalRatio ?? 0,
                        moveOutRate: dataForYearBeforeReforecastUnitMap.get(unitType.unitTypeName)?.moveOutRate ?? 0,
                        occupancyRate: dataForYearBeforeReforecastUnitMap.get(unitType.unitTypeName)?.occupancyRate ?? 0,
                        moveInCount: dataForYearBeforeReforecastUnitMap.get(unitType.unitTypeName)?.moveInCount ?? 0,
                    };
                }
                const builtData = buildFullUnitTypeData({
                    ...unitType,
                    previousVersionLastMonthData,
                    versionType: yearData.version,
                    year: yearData.year
                }, true);
                builtHistoricalRows.push(builtData);
            }
            const historicalTableRows = buildTableData(builtHistoricalRows);
            historicals.push(...historicalTableRows);
        }

        let dataForPreviousYear;
        if (versionType === VersionType.Reforecast) {
            dataForPreviousYear = unitVersionData.find(x => x.version === VersionType.Actuals && x.year === year - 1);
        } else {
            dataForPreviousYear = unitVersionData.find(x => x.version === VersionType.Reforecast);
        }

        const dataForPreviousYearUnitMap = new Map<string, MetricSingleValues>();
        for (const unitType of dataForPreviousYear?.unitTypes ?? []) {
            dataForPreviousYearUnitMap.set(unitType.unitTypeName, {
                leaseExpirationCount: unitType.leaseExpirationCount[11] ?? 0,
                renewalRatio: unitType.renewalRatio[11] ?? 0,
                moveOutRate: unitType.moveOutRate[11] ?? 0,
                occupancyRate: unitType.occupancyRate[11] ?? 0,
                moveInCount: unitType.moveInCount[11] ?? 0,
            });
        }

        const fullUnitTypeData: FullUnitTypeData[] = [];
        for (const unitType of dataForCurrentYear.unitTypes) {
            const prevYearDataForUnitType = dataForPreviousYearUnitMap.get(unitType.unitTypeName);
            const builtData = buildFullUnitTypeData({
                ...unitType,
                previousVersionLastMonthData: {
                    leaseExpirationCount: prevYearDataForUnitType?.leaseExpirationCount ?? 0,
                    renewalRatio: prevYearDataForUnitType?.renewalRatio ?? 0,
                    moveOutRate: prevYearDataForUnitType?.moveOutRate ?? 0,
                    occupancyRate: prevYearDataForUnitType?.occupancyRate ?? 0,
                    moveInCount: prevYearDataForUnitType?.moveInCount ?? 0,
                },
                versionType: versionType,
                year: year
            }, true);
            fullUnitTypeData.push(builtData)
        }

        setHistoricalTableRows(historicals);
        setFullUnitData(fullUnitTypeData);
    }, [unitVersionData]);

    useEffect(() => {
        const filteredTableRows = buildTableData(fullUnitData);
        const filteredHistoricalRows = getFilteredHistoricalRows(historicalTableRows, visibleMetrics);
        setTableRows([...filteredTableRows, ...filteredHistoricalRows]);
    }, [fullUnitData, visibleMetrics, filterText]);

    const isFiltered = useMemo(() => {
        return filterText.trim() !== "";
    }, [filterText]);

    function buildTableData(unitTypes: FullUnitTypeData[]): CompoundSimpleHotRow[] {
        const childRows: CompoundSimpleHotRow[] = [];
        const allBuiltUnitTypeData: FullUnitTypeData[] = [];
        const sortedUnitTypes = unitTypes.sortBy("unitTypeName");
        const filteredUnitTypes = sortedUnitTypes.filter(x => x.unitTypeName.toLowerCase().includes(filterText.toLowerCase().trim()));

        for (const unitType of filteredUnitTypes) {
            allBuiltUnitTypeData.push(unitType);
            const {metricRows, extraData} = buildTableRowValues(unitType, visibleMetrics);
            const unitRow: CompoundSimpleHotRow = {
                isCompound: true,
                verticalColumnMerges: [0],
                values: metricRows,
                extraData: {...extraData},
            };
            childRows.push(unitRow);
        }

        if (isFiltered) {
            return childRows;
        }

        const builtSummaryData = buildSummaryRow(allBuiltUnitTypeData, property);
        const {metricRows, extraData} = buildTableRowValues(builtSummaryData, visibleMetrics);
        const summaryRow: CompoundSimpleHotRow = {
            isCompound: true,
            verticalColumnMerges: [0],
            values: metricRows,
            children: childRows,
            extraData: {...extraData},
        };

        return [summaryRow];
    }

    function handleRoundAllValues() {
        const startMonthIndex = versionType === VersionType.Reforecast ? property.reforecastStartMonthIndex : 0;
        const changes = roundAllValuesToWholeUnits(fullUnitData, startMonthIndex, versionType);

        if (changes.length === 0) {
            toast.info("Every value is already on a whole unit");
            return;
        }

        onChange?.(changes, true);
    }

    const issues = useMemo<IMetricIssue[]>(() => {
        const negativeMoveIns: IMetricIssue[] = [];
        const startingMonth = versionType === VersionType.Reforecast ? property.reforecastStartMonthIndex : 0;

        for (const unitType of fullUnitData) {
            for (let monthIndex = startingMonth; monthIndex < 12; monthIndex++) {
                const moveIns = unitType.counts.moveInCount[monthIndex];
                if (moveIns !== undefined && moveIns < 0) {
                    negativeMoveIns.push({
                        issueType: "negativeMoveIns",
                        unitTypeName: unitType.unitTypeName,
                        unitTypeCount: unitType.unitTypeCount,
                        monthIndex: monthIndex,
                        counts: unitType.counts,
                        rates: unitType.rates,
                        previousVersionLastMonthData: unitType.previousVersionLastMonthData,
                    });
                }
            }
        }
        if (issuesToSolve.current && negativeMoveIns.length === 0) {
            setTimeout(() => {
                celebrate(CONFETTI_CONFIG);
                celebrate({...CONFETTI_CONFIG, startVelocity: 5, particleCount: 30});
            }, 500);
            toast.success("All issues resolved!");
            issuesToSolve.current = false;
        } else if (negativeMoveIns.length > 0) {
            issuesToSolve.current = true;
        }
        return negativeMoveIns;
    }, [fullUnitData]);

    const columns = useMemo<SimpleHotColumnSettings[]>(() => {
        if (showAsRates === true) {
            return [
                {data: "unitTypeName"},
                {data: "metricLabel"},
                {data: "rateValues.0", type: "numeric"},
                {data: "rateValues.1", type: "numeric"},
                {data: "rateValues.2", type: "numeric"},
                {data: "rateValues.3", type: "numeric"},
                {data: "rateValues.4", type: "numeric"},
                {data: "rateValues.5", type: "numeric"},
                {data: "rateValues.6", type: "numeric"},
                {data: "rateValues.7", type: "numeric"},
                {data: "rateValues.8", type: "numeric"},
                {data: "rateValues.9", type: "numeric"},
                {data: "rateValues.10", type: "numeric"},
                {data: "rateValues.11", type: "numeric"},
                {data: "rateAverages", type: "numeric"},
                {data: "rateTotals", type: "numeric"},
            ];
        } else {
            return [
                {data: "unitTypeName"},
                {data: "metricLabel"},
                {data: "countValues.0", type: "numeric"},
                {data: "countValues.1", type: "numeric"},
                {data: "countValues.2", type: "numeric"},
                {data: "countValues.3", type: "numeric"},
                {data: "countValues.4", type: "numeric"},
                {data: "countValues.5", type: "numeric"},
                {data: "countValues.6", type: "numeric"},
                {data: "countValues.7", type: "numeric"},
                {data: "countValues.8", type: "numeric"},
                {data: "countValues.9", type: "numeric"},
                {data: "countValues.10", type: "numeric"},
                {data: "countValues.11", type: "numeric"},
                {data: "countAverages", type: "numeric"},
                {data: "countTotals", type: "numeric"},
            ];
        }
    }, [showAsRates]);

    return (
        <>
            <SimpleHoTWrapperControls>
                <div className={css.controls}>
                    {autoRound &&
                        <Tooltip
                            size="medium"
                            placement="bottom"
                            content="Entered values will automatically round to the nearest whole unit."
                            zIndex={10}
                        >
                            <Tag
                                isPill
                                hue={COLORS.ROYAL_100}
                                size="small"
                                className={css.autoRoundIndicator}
                                onClick={() => setSettingsOpen(!settingsOpen)}
                            >
                                Auto-Rounding Enabled
                            </Tag>
                        </Tooltip>
                    }
                    <Field className={`${css.filterField} ${filterText.length > 0 && css.isClearable}`}>
                        <MediaInput
                            start={<SearchIcon />}
                            placeholder="Search Unit Types"
                            value={filterText}
                            onChange={(evt) => {
                                setFilterText(evt.target.value);
                            }}
                            aria-label="search table"
                            end={<IconButton size="small" onClick={() => setFilterText("")}><Close /></IconButton>}
                        />
                    </Field>
                    {!tableLocked && issues.length > 0 &&
                        <MetricIssues
                            property={property}
                            issues={issues}
                            onChange={(monthIndex, unitTypeName, rate, metric) => {
                                const unitTypeCalcInputs: FullUnitTypeDataCalcInput[] = [];
                                for (const unitType of fullUnitData) {
                                    const updatedUnitTypeData = {
                                        ...unitType,
                                        leaseExpirationCount: unitType.rates.leaseExpirationCount,
                                        moveInCount: unitType.rates.moveInCount,
                                        moveOutRate: unitType.rates.moveOutRate,
                                        occupancyRate: unitType.rates.occupancyRate,
                                        renewalRatio: unitType.rates.renewalRatio,
                                    };

                                    if (unitType.unitTypeName === unitTypeName) {
                                        unitType.rates[metric][monthIndex] = rate ?? 0;

                                        if (metric === OriginalRevenueOperationalMetricValidKey.RenewalRatio) {
                                            unitType.rates.moveOutRate[monthIndex] = 100 - (rate ?? 0);
                                        }
                                    }
                                    unitTypeCalcInputs.push(updatedUnitTypeData);
                                }

                                // the user started solving issues with the metric issue solver, so prime the
                                // UI state to reward them when they're done
                                issuesToSolve.current = true;
                                // since we only ever present solutions through percentages, I'm passing 'true' to buildFullUnitTypeData
                                // instead of passing showAsRates, because in this context anything passed to it should be treating as
                                // a percentage, not a count
                                const updatedUnitTypeData = unitTypeCalcInputs.map(x => buildFullUnitTypeData(x, true));
                                setFullUnitData(updatedUnitTypeData);

                                // send data to the backend
                                if (rate !== null) {
                                    const changes: IBatchUpsertInput[] = [{
                                        fieldName: metric,
                                        unitTypeName,
                                        monthIndex,
                                        value: String(rate / 100.0),
                                    }];
                                    // if the user is solving it by changing renewals, we need to adjust the move out rate to fit
                                    if (metric === OriginalRevenueOperationalMetricValidKey.RenewalRatio) {
                                        changes.push({
                                            fieldName: OriginalRevenueOperationalMetricValidKey.MoveOutRate,
                                            unitTypeName,
                                            monthIndex,
                                            value: String((100 - rate) / 100.0),
                                        });
                                    }
                                    onChange?.(changes);
                                }
                            }}
                        />
                    }

                    <div className={css.actions}>
                        {controlsSlot}
                        <Button
                            ref={settingsButtonRef}
                            className={css.settingsButton}
                            size="small"
                            isBasic
                            onClick={() => setSettingsOpen(!settingsOpen)}
                        >
                            <Button.StartIcon><Settings /></Button.StartIcon>
                            Table Settings
                        </Button>
                    </div>
                </div>
            </SimpleHoTWrapperControls>
            <MemoizedSimpleHoTWrapper
                withControls
                dataLoading={isLoading}
                nestedRows={!isFiltered}
                rows={tableRows}
                columns={columns}
                openSectionsPersistOnRender
                hotProps={{
                    height: "100%",
                    stretchH: "all",
                    colHeaders: ["", "", ...getMonths(property), "Average", "Total"],
                    colWidths: [120, 60],
                    autoRowSize: false,
                    autoColumnSize: false,
                    rowHeights: 48,
                    fixedColumnsStart: 2,
                    fixedRowsTop: isFiltered ? 0 : visibleMetrics.size,
                    viewportRowRenderingOffset: 150,
                }}
                beforeChange={changes => {
                    // dont let the user make changes to both summary row and unit type rows at the same time
                    // if we detect summary row changes in the changes array, filter out any changes to children
                    // and only operate on those
                    let computedChanges = changes;
                    const changesToSummaryRows = changes.filter(x => x.row.children !== undefined);

                    // to maintain the undo/redo stack, we dont want to actually send the summary row change to Hot
                    // instead, we are going to synthesize child row updates and send those, so if the user tries to undo or redo,
                    // they'll actually get the previous table state, and not just force all unit types to be the previous summary row value
                    if (changesToSummaryRows.length > 0) {
                        computedChanges = [];
                        for (const change of changesToSummaryRows) {
                            const children = (change.row.children ?? []) as CompoundSimpleHotRow[];
                            const {col, compoundRowValuesIndex, newValue} = change;
                            for (const child of children) {
                                let previousValue = 0;
                                const colNumber = colPropToMonth(col);
                                if (compoundRowValuesIndex !== undefined) {
                                    const valuesToUse = showAsRates ? child.values[compoundRowValuesIndex]?.["rateValues"] : child.values[compoundRowValuesIndex]?.["countValues"];
                                    if (Array.isArray(valuesToUse)) {
                                        previousValue = Number(valuesToUse[colNumber] ?? 0);
                                    }
                                }
                                // this is our new synthetic simplehotchange that uses the newValue from the summary row the user just updated,
                                // BUT we need to make sure the previousValue comes from the corresponding unitType row cell, since that is what
                                // gets loaded into the Hot undoRedo stack
                                computedChanges.push({
                                    col: col,
                                    newValue: newValue,
                                    previousValue: previousValue,
                                    row: child,
                                    compoundRowValuesIndex: compoundRowValuesIndex,
                                    rowId: change.rowId
                                });
                            }
                        }
                    }

                    // if the user hits the delete key on a cell, we treat that as a 0 so substitute those entries with a 0
                    computedChanges.forEach(x => {
                        if (x.newValue === undefined || x.newValue === null) {
                            x.newValue = 0;
                        }
                    });
                    if (computedChanges.every(x => validateOccupancyManagerInput(x.newValue))) {
                        return roundToWholeUnitsRef.current ? applyWholeUnitRounding(computedChanges, showAsRates) : computedChanges;
                    } else {
                        toast.error("Please only enter numbers");
                        return [];
                    }
                }}
                onChange={changes => {
                    const newData: FullUnitTypeDataCalcInput[] = [];
                    const changedRows = changes.map(x => x.row);
                    const changedCols = changes.map(x => colPropToMonth(String(x.col)));
                    const changesToSummaryRows = changes.filter(x => x.row.children !== undefined);
                    const primaryTableRow = tableRows[0];
                    if (!primaryTableRow) return;

                    // if the table is filtered, we don't show the summary row, meaning no parent row is present
                    // that means we can skip checking any of the parent/child stuff and just go right to generating new summaries
                    if (isFiltered) {
                        const updatedData = calculateChangesFromTable(primaryTableRow, changedRows, changedCols, showAsRates, versionType, year);
                        if (updatedData) {
                            newData.push(updatedData);
                        }
                    } else {
                        if (!primaryTableRow.children) return;

                        for (const child of primaryTableRow.children) {
                            // if we are making changes to summary row cells, loop through those changes and populate
                            // their corresponding coordinates in the child rows before recalculating summaries
                            if (changesToSummaryRows.length > 0) {
                                const childRowUpdates = updateUnitTypeCellsAfterSummaryRowChange(changes, child, changesToSummaryRows, showAsRates);
                                changedRows.push(...childRowUpdates);
                            }

                            // recalculate summaries for full table with new values
                            const updatedData = calculateChangesFromTable(child, changedRows, changedCols, showAsRates, versionType, year);
                            if (updatedData) {
                                newData.push(updatedData);
                            }
                        }
                    }

                    // store a copy of our full table data, so the user can filter without losing anything and we can restore it later
                    const fullUnitData = newData.map(x => buildFullUnitTypeData(x, showAsRates));
                    setFullUnitData(fullUnitData);

                    // now that we've fully handled any data mutations in the table, build out our API payload to save anything that actually needs to be saved
                    const changesArray = buildAPIPayloadFromChanges(changes, newData, showAsRates);
                    onChange?.(changesArray);
                }}
                cellStyler={(rowNum, col, rowData, cellValue, valuesIndex?: number) => {
                    const style: SimpleHoTCellStyle = {};
                    const rfcstStartMonthCol = MONTH_COLS[property.reforecastStartMonthIndex] ?? 0;
                    const writableStartMonth = versionType === VersionType.Reforecast ? rfcstStartMonthCol : -1;

                    style.cssClasses = style.cssClasses === undefined ? [] : [...style.cssClasses];

                    if (valuesIndex === undefined) {
                        return style;
                    }

                    const row = rowData as CompoundSimpleHotRow;
                    const metric = row.values[valuesIndex]?.["metricKey"] as AvailableMetrics;

                    const isSummaryRow = row.children !== undefined;
                    // basically, consider something a historical if its from actuals OR if its rfcst and we're looking at the bdgt season
                    const isHistoricalRow = row?.extraData?.["versionType"] as VersionType === VersionType.Actuals
                        || (versionType === VersionType.Budget && row?.extraData?.["versionType"] as VersionType === VersionType.Reforecast);

                    style.isReadOnly = col < writableStartMonth;

                    if (isHistoricalRow) {
                        style.cssClasses.push(css.slightlyDarkerGreyBg);
                        style.isReadOnly = true;
                    }

                    if ([LABEL_COL, METRIC_COL, AVG_COL, TOTAL_COL].includes(col)) {
                        style.isReadOnly = true;
                        style.cssClasses.push(css.greyBg, css.boldText);

                        if (rowNum >= visibleMetrics.size || isFiltered) {
                            style.cssClasses.push(css.slightlyDarkerGreyBg);
                        }
                    }

                    if (col === LABEL_COL) {
                        style.cssClasses.push(css.labelCell);
                        if (visibleMetrics.size > 1) {
                            style.cssClasses.push(css.thickBorderBottom);
                        }
                        if (!isSummaryRow || isFiltered) {
                            style.cssClasses.push(css.childLabelCell);
                        }
                    }

                    // add a thick border between unit type groups
                    if (valuesIndex === visibleMetrics.size - 1 && visibleMetrics.size > 1) {
                        style.cssClasses.push(css.thickBorderBottom);
                    }

                    if (col > METRIC_COL) {
                        if (metric === OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount
                            || metric === OriginalRevenueOperationalMetricValidKey.MoveInCount
                            || !showAsRates
                            || col === TOTAL_COL
                        ) {
                            style.numericFormat = "0";
                        } else {
                            style.numericFormat = "0,0.0";
                            style.cssClasses.push("percentage-cell");
                        }
                    }

                    // if we aren't filtering, meaning the summary row is showing, AND the first row is expirations, disable editing
                    if (metric === OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount && rowNum === 0 && !isFiltered) {
                        style.isReadOnly = true;
                    }

                    // move ins are never editable
                    if (metric === OriginalRevenueOperationalMetricValidKey.MoveInCount) {
                        style.isReadOnly = true;
                        if (!isHistoricalRow && col >= writableStartMonth && typeof cellValue === "number" && cellValue < 0) {
                            style.cssClasses.push(css.cellIsError);
                        }
                    }

                    // prevent summary row from being edited if displaying values as counts
                    // it does weird stuff, and we would have to decide if we are setting the number they enter
                    // for every unit type exactly, or if we are going to divide it across all unit types
                    if (!showAsRates && rowNum < visibleMetrics.size && !isFiltered) {
                        style.isReadOnly = true;
                    }

                    if (col === writableStartMonth) {
                        style.cssClasses.push(css.blueBorderLeft);
                    }

                    if (tableLocked) {
                        style.isReadOnly = true;
                    }

                    return style;
                }}
                customRenderer={(simpleHRow, _col, compoundRowValuesIndex) => {
                    const rfcstStartMonthCol = MONTH_COLS[property.reforecastStartMonthIndex] ?? 0;
                    const writableStartMonth = versionType === VersionType.Reforecast ? rfcstStartMonthCol : -1;

                    if (!simpleHRow.isCompound || compoundRowValuesIndex === undefined) {
                        return;
                    }

                    return function(this: Handsontable, hot, td, row, col, prop, value, cellProperties): void {
                        const metric = simpleHRow.values[compoundRowValuesIndex]?.["metricKey"] as OriginalRevenueOperationalMetricValidKey;
                        const unitCount = simpleHRow.extraData?.["unitTypeCount"] as number;
                        const renderer: BaseRenderer = Handsontable.renderers.NumericRenderer;

                        const extraData = simpleHRow?.extraData;
                        if (!extraData) {
                            return;
                        }

                        // basically, consider something a historical if its from actuals OR if its rfcst and we're looking at the bdgt season
                        const isActualsRow = extraData["versionType"] as VersionType === VersionType.Actuals;

                        if (col === LABEL_COL) {
                            const unitCountText = document.createElement("span");
                            unitCountText.className = css.subText;
                            unitCountText.textContent = `${unitCount.toLocaleString()} Unit${unitCount !== 1 ? "s" : ""}`;
                            td.append(unitCountText);
                        }

                        // any of the columns that actually have numbers, I want them to display rounded, so we do it here to avoid altering the actual data
                        if (col > METRIC_COL) {
                            renderer.apply(this, [hot, td, row, col, prop, round(value ?? 0, 1), cellProperties]);
                        }
                        // RENEWAL RATIO SPECIFIC CONFIGS
                        if (metric === OriginalRevenueOperationalMetricValidKey.RenewalRatio && col > LABEL_COL) {
                            td.classList.add(css.multiLineCell);
                            const moveOutsSubText = document.createElement("span");
                            if (col === METRIC_COL) {
                                moveOutsSubText.innerText = "Move Outs";
                            } else {
                                const keyForMonthlyValues = (showAsRates ? extraData["rates"] : extraData["counts"]) as MetricArrays;
                                const monthCol = col - 2;
                                const moveOutsForMonth = keyForMonthlyValues.moveOutRate[monthCol] as number;
                                let moveOutDisplayValue = moveOutsForMonth;

                                if (col === AVG_COL) {
                                    const averages = (showAsRates ? extraData["rateAverages"] : extraData["countAverages"]) as MetricSingleValues;
                                    moveOutDisplayValue = averages.moveOutRate;
                                } else if (col === TOTAL_COL) {
                                    const totals = (showAsRates ? extraData["rateTotals"] : extraData["countTotals"]) as MetricSingleValues;
                                    moveOutDisplayValue = totals.moveOutRate;
                                }
                                moveOutsSubText.innerText = `${round(moveOutDisplayValue ?? 0, 1)}`;
                                if (showAsRates && col !== TOTAL_COL) {
                                    const subtextPercentSymbol = document.createElement("span");
                                    subtextPercentSymbol.innerText = "%";
                                    subtextPercentSymbol.classList.add(css.multiLineCellSubtextPercentSign);
                                    moveOutsSubText.append(subtextPercentSymbol);
                                }
                            }
                            moveOutsSubText.classList.add(css.subText);
                            td.append(moveOutsSubText);
                        } else if (metric === OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount && col > LABEL_COL && !isActualsRow) {
                            td.classList.add(css.multiLineCell);
                            const percentOfUnitsSubText = document.createElement("span");
                            if (col === METRIC_COL) {
                                percentOfUnitsSubText.innerText = "% of Units";
                            } else {
                                let expirations = (extraData["counts"] as MetricArrays).leaseExpirationCount[col - 2] ?? 0;
                                if (col === AVG_COL) {
                                    expirations = (extraData["countAverages"] as MetricSingleValues).leaseExpirationCount;
                                } else if (col === TOTAL_COL) {
                                    expirations = (extraData["countTotals"] as MetricSingleValues).leaseExpirationCount;
                                }
                                const percentOfUnits = getExpirationPercentOfUnits(expirations, unitCount);
                                if (percentOfUnits === null) {
                                    percentOfUnitsSubText.innerText = "N/A";
                                } else {
                                    percentOfUnitsSubText.innerText = `${round(percentOfUnits, 1)}`;
                                    const subtextPercentSymbol = document.createElement("span");
                                    subtextPercentSymbol.innerText = "%";
                                    subtextPercentSymbol.classList.add(css.multiLineCellSubtextPercentSign);
                                    percentOfUnitsSubText.append(subtextPercentSymbol);
                                }
                            }
                            percentOfUnitsSubText.classList.add(css.subText);
                            td.append(percentOfUnitsSubText);
                        } else if (metric === OriginalRevenueOperationalMetricValidKey.OccupancyRate && col === TOTAL_COL) {
                            td.innerText = "N/A";
                        } else if (metric === OriginalRevenueOperationalMetricValidKey.MoveInCount) {
                            // currently, I'm not showing the calculated move ins for historicals, but we could if we want
                            if (isActualsRow && col > METRIC_COL) {
                                td.innerText = "-";
                            } else if (col >= writableStartMonth && col < AVG_COL && value < 0) {
                                // we display values as rounded EXCEPT when they are negative move ins
                                // this avoids showing a red cell with a 0 in it, because that looks weird
                                renderer.apply(this, [hot, td, row, col, prop, value, cellProperties]);
                            } else if (col < writableStartMonth && value < 0) {
                                td.innerText = "0";
                            }
                        }
                    };
                }}
            />
            {settingsOpen && settingsButtonRef.current &&
                <TooltipModal
                    referenceElement={settingsButtonRef.current}
                    onClose={() => setSettingsOpen(false)}
                    placement="start-top"
                    className={css.settingsMenu}
                >
                    <TooltipModal.Body className={css.settingsBody}>
                        <div className={css.itemGroup}>
                            <h5 className={css.itemGroupLabel}>Visible Metrics</h5>
                            {
                                Array.from(metricToLabelMap.entries()).filter(([key]) => key !== OriginalRevenueOperationalMetricValidKey.MoveOutRate).map(([key, label]) =>
                                    <Field key={key}>
                                        <Toggle
                                            checked={visibleMetrics.has(key)}
                                            onChange={() => {
                                                setVisibleMetrics(prev => {
                                                    const newVisibleMetrics = new Set(prev);
                                                    if (prev.has(key)) {
                                                        newVisibleMetrics.delete(key);
                                                    } else {
                                                        newVisibleMetrics.add(key);
                                                    }
                                                    return newVisibleMetrics;
                                                });
                                            }}
                                        >
                                            <Label>{key === OriginalRevenueOperationalMetricValidKey.RenewalRatio ? "Renewals & Move Outs" : label}</Label>
                                        </Toggle>
                                    </Field>
                                )
                            }
                        </div>
                        <div className={css.itemGroup}>
                            <VZTwoButtonGroup
                                fieldLabel={"View As"}
                                firstLabel={"Percents"}
                                secondLabel={"Counts"}
                                defaultIsFirstOptionSelected={showAsRates}
                                onChange={setShowAsRates}
                            />
                        </div>
                        <div className={css.itemGroup}>
                            <Field>
                                <Toggle
                                    checked={autoRound}
                                    onChange={(evt) => {
                                        setAutoRound(evt.target.checked);
                                        sessionStorage.setItem(USE_AUTO_ROUNDING_KEY, evt.target.checked === true ? "true" : "false");
                                    }}
                                    aria-label="toggle rounding to whole units"
                                >
                                    <Label>Auto-Round to Whole Units</Label>
                                    <Hint>Automatically round entered values to the nearest whole unit.</Hint>
                                </Toggle>
                            </Field>
                            {!tableLocked &&
                                <Button isPrimary size="small" onClick={() => setShowRoundAllConfirmation(true)}>
                                    <div className={css.roundingIcon}>
                                        <DecimalFill className={css.decimalIcon} />
                                        <ArrowLeft className={css.arrowIcon} />
                                    </div>
                                    Round All {property.isAcquisitionProperty ? getVersionLabel(versionType, year, property) : (versionType === VersionType.Reforecast ? "Reforecast" : "Budget")} Values
                                </Button>
                            }
                        </div>
                    </TooltipModal.Body>
                    <TooltipModal.Close aria-label="Close" />
                </TooltipModal>
            }
            {showRoundAllConfirmation &&
                <Modal onClose={() => setShowRoundAllConfirmation(false)}>
                    <Header>Round all values to whole units?</Header>
                    <Body>
                        <p>
                            Every Renewal, Move Out, and Occupancy value on this table will be adjusted to the percentage closest to its current value that works out to a whole number of units.
                        </p>
                        <p>
                            This action cannot be undone - proceed?
                        </p>
                    </Body>
                    <Footer>
                        <FooterItem>
                            <Button isBasic onClick={() => setShowRoundAllConfirmation(false)}>Cancel</Button>
                        </FooterItem>
                        <FooterItem>
                            <Button
                                isPrimary
                                onClick={() => {
                                    setShowRoundAllConfirmation(false);
                                    handleRoundAllValues();
                                }}
                            >
                                Round All Values
                            </Button>
                        </FooterItem>
                    </Footer>
                    <ModalClose aria-label="Close round all values modal" />
                </Modal>
            }
        </>
    );
}

const MemoizedSimpleHoTWrapper = memo(SimpleHoTWrapper, (prevProps, nextProps) => {
    return prevProps.rows === nextProps.rows &&
           prevProps.dataLoading === nextProps.dataLoading &&
           prevProps.columns === nextProps.columns;
});
